import {
  GCalEvent,
  CalendarWidgetEvent,
  CalendarListItem,
} from "../Contract/GoogleTypes";

export type GoogleCalColorsResponse = {
  calendar?: Record<string, { background?: string; foreground?: string }>;
  event?: Record<string, { background?: string; foreground?: string }>;
};

export const fetchGoogleCalColors = async (
  token: string,
): Promise<GoogleCalColorsResponse> => {
  const res = await fetch("https://www.googleapis.com/calendar/v3/colors", {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`colors error (${res.status}): ${text}`);
  }

  return (await res.json()) as GoogleCalColorsResponse;
};

export function loadScriptOnce(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(
      `script[src="${src}"]`,
    ) as HTMLScriptElement | null;

    // If it's already loaded, resolve immediately
    if (existing && (existing as any)._loaded) {
      resolve();
      return;
    }

    const onLoad = () => {
      if (script) (script as any)._loaded = true;
      resolve();
    };

    const onError = () => reject(new Error(`Failed to load script: ${src}`));

    const script = existing ?? document.createElement("script");
    if (!existing) {
      script.src = src;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });
  });
}

export function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
export function addDays(d: Date, days: number) {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
}
export function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
export function formatMonthUpper(d: Date) {
  return d.toLocaleDateString(undefined, { month: "long" }).toUpperCase();
}
export function formatTime(d: Date) {
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function parseGCalEvent(
  ev: GCalEvent,
  sourceCalendarId?: string,
): CalendarWidgetEvent | null {
  if (!ev.id || ev.status === "cancelled") return null;

  const allDay = Boolean(ev.start?.date && !ev.start?.dateTime);
  const startStr = ev.start?.dateTime ?? ev.start?.date;
  const endStr = ev.end?.dateTime ?? ev.end?.date;
  if (!startStr) return null;

  return {
    id: ev.id,
    sourceCalendarId,
    eventColorId: ev.colorId,
    title: ev.summary || "(No title)",
    start: new Date(startStr),
    end: endStr ? new Date(endStr) : undefined,
    allDay,
    location: ev.location,
  };
}

export function getMonthGrid(baseDate: Date, weekStartsOnMonday = true) {
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);

  const jsDayFirst = first.getDay(); // 0 Sun ... 6 Sat
  const startOffset = weekStartsOnMonday ? (jsDayFirst + 6) % 7 : jsDayFirst; // Mon=0
  const gridStart = addDays(first, -startOffset);

  const days: Array<{ date: Date; inMonth: boolean }> = [];
  for (let i = 0; i < 42; i++) {
    const d = addDays(gridStart, i);
    days.push({ date: d, inMonth: d.getMonth() === month });
  }

  return { first, last, days };
}
export const listCalendars = async (
  token?: string,
): Promise<CalendarListItem[]> => {
  const url = `https://www.googleapis.com/calendar/v3/users/me/calendarList?maxResults=250`;

  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`calendarList error (${res.status}): ${text}`);
  }

  const data: { items?: CalendarListItem[] } = await res.json();
  // Only calendars the user can read
  return (data.items ?? []).filter(
    (c) => c.id && c.accessRole && c.accessRole !== "none",
  );
};
